import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import SEO from '../components/SEO';
import ArticleCard from '../components/ArticleCard';
import HilltopBanner from '../components/HilltopBanner';

function ArticlesListing() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [articlesPerPage] = useState(6);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await apiService.getArticles();
        if (response.success) {
          setArticles(response.data);
        }
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const indexOfLastArticle = currentPage * articlesPerPage;
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
  const currentArticles = articles.slice(indexOfFirstArticle, indexOfLastArticle);
  const totalPages = Math.ceil(articles.length / articlesPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20 px-6 md:px-12">
      <SEO
        title="CalcBlog Registry - Practical Insights & Compute Manuals"
        description="Access our full registry of data-driven insights, optimization protocols, and interactive calculation manuals."
        keywords="calcblog registry, article database, compute manuals, performance insights"
        toolType="blog"
      />

      <div className="container mx-auto">
        {/* Header */}
        <div className="mb-20 space-y-4">
          <span className="label-caps text-brand">Articles</span>
          <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tight leading-[1.1]">
            ALL ARTICLES
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl leading-relaxed">
            Browse through all our insightful articles and tutorials.
          </p>
        </div>

        {/* Ad Section */}
        <div className="flex justify-center py-8 border-y border-white/5 mb-12">
          <HilltopBanner />
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-14 mb-20">
          {currentArticles.map((article, idx) => (
            <ArticleCard key={article.id} article={article} index={indexOfFirstArticle + idx} />
          ))}
        </div>

        {/* Pagination & Footer Info */}
        <div className="border-t border-white/5 pt-12 space-y-12">
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`w-12 h-12 flex items-center justify-center rounded-full glass transition-all ${currentPage === 1
                  ? 'opacity-20 cursor-not-allowed'
                  : 'text-brand hover:bg-brand/10 border-brand/40'
                  }`}
              >
                <span className="material-symbols-outlined">chevron_left</span>
              </button>

              <div className="flex items-center gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                  <button
                    key={number}
                    onClick={() => paginate(number)}
                    className={`min-w-[48px] h-12 flex items-center justify-center rounded-full font-bold text-xs tracking-widest transition-all ${currentPage === number
                      ? 'bg-brand text-[#050805] shadow-[0_0_20px_rgba(0,255,136,0.5)]'
                      : 'glass text-slate-400 hover:text-brand'
                      }`}
                  >
                    {number.toString().padStart(2, '0')}
                  </button>
                ))}
              </div>

              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`w-12 h-12 flex items-center justify-center rounded-full glass transition-all ${currentPage === totalPages
                  ? 'opacity-20 cursor-not-allowed'
                  : 'text-brand hover:bg-brand/10 border-brand/40'
                  }`}
              >
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
          )}
        </div>

        {/* Empty State */}
        {articles.length === 0 && (
          <div className="text-center py-32 glass rounded-2xl">
            <span className="material-symbols-outlined text-6xl text-slate-600 mb-6">database_off</span>
            <h3 className="text-2xl font-bold text-brand mb-2">REGISTRY_NOT_FOUND</h3>
            <p className="text-slate-400">Check back later for updated data nodes.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ArticlesListing;
